import { useId, useState, type FormEvent } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import type { WebsiteRead, WebsiteWrite } from '@/api/schema';
import { useToast } from '@/components/feedback/Toast';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { EditorColumns, EditorPage } from '@/components/layout/EditorPage';
import { Panel } from '@/components/layout/Page';
import { useUnsavedChanges } from '@/lib/useUnsavedChanges';

import { RichTextEditor } from '@/components/ui/RichTextEditor';
import { apiUrl } from '@/lib/env';
import type { WebsiteSectionConfig } from './sections';
import { uploadWebsiteImage, useSaveWebsiteEntry } from './websiteApi';
import { WebsiteImageField, WebsiteSelect, WebsiteText } from './WebsiteFields';
import styles from './WebsiteContent.module.css';

const BLOG_CATEGORIES = [
  'Career planning',
  'Working overseas',
  'International experience',
  'Application essentials',
  'Interview preparation',
  'For employers',
].map((label) => ({ label, value: label }));
const AUDIENCES = [
  { value: 'candidate', label: 'For candidates' },
  { value: 'employer', label: 'For employers' },
] as const;
const RESOURCE_CARDS = [
  { value: 'application', label: 'Application guide' },
  { value: 'interview', label: 'Interview guide' },
  { value: 'hiring', label: 'Hiring guide' },
] as const;
const FORMATS = [
  { value: 'webinars', label: 'Webinar' },
  { value: 'interviews', label: 'Interview' },
  { value: 'podcasts', label: 'Podcast' },
] as const;
const EVENT_ACTIONS = [
  { value: 'register', label: 'Register' },
  { value: 'watch', label: 'Watch' },
  { value: 'listen', label: 'Listen' },
  { value: 'read', label: 'Read' },
] as const;
function dateTimeLocal(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 16);
}

export function WebsiteEditor({
  initial,
  section,
  onClose,
}: {
  initial: WebsiteRead;
  section: WebsiteSectionConfig;
  onClose: () => void;
}) {
  const formId = useId();
  const { id, ...fields } = initial;
  const [draft, setDraft] = useState<WebsiteWrite>(fields);
  const [tags, setTags] = useState((fields.tags ?? []).join(', '));
  const [uploads, setUploads] = useState(0);
  const [error, setError] = useState('');
  const [changed, setChanged] = useState(false);
  const save = useSaveWebsiteEntry();
  const toast = useToast();
  const isArticle = draft.kind === 'blog' || draft.kind === 'press';
  const hasBody = draft.kind !== 'faq';
  const busy = save.isPending || uploads > 0;
  const unsaved = useUnsavedChanges(changed || busy);
  const source = draft.sources?.[0];
  const change = (patch: Partial<WebsiteWrite>) => {
    setDraft((current) => ({ ...current, ...patch }));
    setChanged(true);
  };
  const onBusy = (amount: number) => {
    setUploads((current) => current + amount);
  };

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const submitter = (event.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null;
    const state = submitter?.value === 'published' ? 'published' : 'draft';
    await saveState(state);
  }

  async function saveState(state: WebsiteWrite['state']) {
    if (busy) return;
    setError('');
    try {
      await save.mutateAsync({
        id,
        entry: {
          ...draft,
          state,
          tags: tags
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean),
        },
      });
      toast.success(state === 'published' ? 'Published on the website.' : 'Draft saved.');
      unsaved.allowSavedExit();
      onClose();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'The entry could not be saved.');
    }
  }

  return (
    <EditorPage
      title={`Edit ${section.item}`}
      description={
        draft.state === 'published'
          ? 'Updates to this published entry appear on the website.'
          : 'This draft is private until you publish it.'
      }
      backTo={section.path}
      backLabel={`Back to ${section.title}`}
      unsaved={unsaved}
      busy={busy}
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {draft.state === 'published' ? (
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => {
                void saveState('draft');
              }}
            >
              Move to draft
            </Button>
          ) : (
            <Button variant="ghost" type="submit" form={formId} value="draft" disabled={busy}>
              Save draft
            </Button>
          )}
          <Button
            type="submit"
            form={formId}
            value="published"
            loading={save.isPending}
            disabled={busy}
          >
            {draft.state === 'published' ? 'Update published entry' : 'Publish'}
          </Button>
        </>
      }
    >
      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
      <form id={formId} onSubmit={(event) => void submit(event)}>
        <EditorColumns
          settings={
            <>
              {hasBody ? (
                <Panel title="Cover image">
                  <WebsiteImageField
                    entryId={id}
                    label="Upload cover image"
                    imageId={draft.cover_image_id ?? null}
                    alt={draft.cover_alt}
                    onChange={(cover_image_id, cover_alt) => {
                      change({ cover_image_id, cover_alt });
                    }}
                    onBusy={onBusy}
                  />
                  {draft.legacy_image && !draft.cover_image_id ? (
                    <p className={styles.help}>This entry uses its existing website cover.</p>
                  ) : null}
                </Panel>
              ) : null}
              <Panel title="Publishing">
                <WebsiteText
                  label="Link name"
                  value={draft.slug}
                  onChange={() => undefined}
                  disabled
                />
                <WebsiteText
                  label="Display order (smaller numbers appear first)"
                  type="number"
                  value={String(draft.sort_order)}
                  onChange={(value) => {
                    change({ sort_order: Number(value) });
                  }}
                />
              </Panel>
              {draft.kind === 'blog' ? (
                <Panel title="Blog settings">
                  <WebsiteSelect
                    label="Category"
                    value={draft.category}
                    options={BLOG_CATEGORIES}
                    onChange={(category) => {
                      change({ category });
                    }}
                  />
                  <WebsiteSelect
                    label="Audience"
                    value={draft.audience}
                    options={AUDIENCES}
                    onChange={(audience) => {
                      change({ audience: audience === '' ? 'candidate' : audience });
                    }}
                  />
                  <WebsiteText
                    label="Tags (separate with commas)"
                    value={tags}
                    onChange={(value) => {
                      setTags(value);
                      setChanged(true);
                    }}
                  />
                  <WebsiteText
                    label="Reading time in minutes"
                    type="number"
                    value={String(draft.read_minutes ?? '')}
                    onChange={(value) => {
                      change({ read_minutes: value ? Number(value) : null });
                    }}
                  />
                  <WebsiteSelect
                    label="Practical resource card"
                    value={draft.resource_card ?? ''}
                    options={RESOURCE_CARDS}
                    onChange={(resourceCard) => {
                      change({ resource_card: resourceCard === '' ? null : resourceCard });
                    }}
                  />
                  {draft.resource_card ? (
                    <>
                      <WebsiteText
                        label="Resource card title"
                        value={draft.resource_title}
                        onChange={(resource_title) => {
                          change({ resource_title });
                        }}
                      />
                      <WebsiteText
                        label="Resource card description"
                        value={draft.resource_summary}
                        onChange={(resource_summary) => {
                          change({ resource_summary });
                        }}
                      />
                      <p className={styles.help}>
                        Publishing fills this slot in Practical resources.
                      </p>
                    </>
                  ) : null}
                </Panel>
              ) : null}
              {draft.kind === 'press' ? (
                <Panel title="Press coverage">
                  <div className={styles.stack}>
                    <WebsiteText
                      label="Publication date"
                      type="date"
                      value={draft.date ?? ''}
                      onChange={(date) => {
                        change({ date: date || null });
                      }}
                    />
                    <WebsiteText
                      label="Category"
                      value={draft.category}
                      onChange={(category) => {
                        change({ category });
                      }}
                    />
                    <Checkbox
                      label="Feature at the top of Press Releases"
                      checked={draft.featured}
                      onChange={(event) => {
                        change({ featured: event.target.checked });
                      }}
                    />
                    <div className={styles.publisher}>
                      <div className={styles.sectionHeading}>
                        <h3>Publisher link</h3>
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={source ? <Trash2 size={14} /> : <Plus size={14} />}
                          onClick={() => {
                            change({
                              sources: source ? [] : [{ name: '', url: '', attribution: '' }],
                            });
                          }}
                        >
                          {source ? 'Remove publisher link' : 'Add publisher link'}
                        </Button>
                      </div>
                      {source ? (
                        <div className={styles.stack}>
                          <WebsiteText
                            label="Publication name"
                            required
                            value={source.name}
                            onChange={(name) => {
                              change({ sources: [{ ...source, name }] });
                            }}
                          />
                          <WebsiteText
                            label="Coverage URL"
                            type="url"
                            required
                            value={source.url}
                            onChange={(url) => {
                              change({ sources: [{ ...source, url }] });
                            }}
                          />
                          <WebsiteText
                            label="Attribution (optional)"
                            value={source.attribution}
                            onChange={(attribution) => {
                              change({ sources: [{ ...source, attribution }] });
                            }}
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                </Panel>
              ) : null}
              {draft.kind === 'event' ? (
                <Panel title="Event settings">
                  <WebsiteSelect
                    label="Format"
                    value={draft.event_format ?? ''}
                    options={FORMATS}
                    onChange={(format) => {
                      change({ event_format: format === '' ? null : format });
                    }}
                  />
                  <WebsiteText
                    label="Event date and time (your time zone)"
                    type="datetime-local"
                    value={dateTimeLocal(draft.starts_at)}
                    onChange={(value) => {
                      change({ starts_at: value ? new Date(value).toISOString() : null });
                    }}
                  />
                  <WebsiteSelect
                    label="Link label"
                    value={draft.event_action}
                    options={EVENT_ACTIONS}
                    onChange={(action) => {
                      change({ event_action: action === '' ? 'register' : action });
                    }}
                  />
                  <WebsiteText
                    label="Registration or recording URL"
                    type="url"
                    value={draft.event_url ?? ''}
                    onChange={(url) => {
                      change({ event_url: url || null });
                    }}
                  />
                </Panel>
              ) : null}
            </>
          }
        >
          <Panel title="Content">
            <WebsiteText
              label={draft.kind === 'faq' ? 'Question' : 'Title'}
              value={draft.title}
              onChange={(title) => {
                change({ title });
              }}
              required
            />
            <WebsiteText
              label={draft.kind === 'faq' ? 'Answer' : 'Summary'}
              value={draft.summary}
              onChange={(summary) => {
                change({ summary });
              }}
              multiline
            />
          </Panel>
          {hasBody ? (
            <RichTextEditor
              imageUrl={(imageId) => apiUrl(`/api/v1/website/images/${imageId}`)}
              selectImage={async (file) => (await uploadWebsiteImage(id, file)).id}
              label={isArticle ? 'Article body' : 'Event details'}
              initial={fields.body ?? []}
              busy={busy}
              onChange={(body) => {
                change({ body });
              }}
              onBusy={onBusy}
            />
          ) : null}
        </EditorColumns>
      </form>
    </EditorPage>
  );
}
